import { useEffect, useState, useCallback } from 'react'
import {
  ArrowLeft, RefreshCw, Clock, User, Users, Calendar,
  CheckCircle2, XCircle, AlertTriangle, ChevronRight, Shield,
  ClipboardList, Activity, FileText,
} from 'lucide-react'
import styles from './DrillDetailPage.module.css'

const DR_API = import.meta.env.VITE_DR_API_BASE_URL ?? ''

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('dr_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface DrillDetail {
  id: number
  title: string
  product_name: string
  drill_type: string
  status: string
  outcome: string | null
  scheduled_date: string
  started_at: string | null
  completed_at: string | null
  duration_minutes: number | null
  sla_compliant: boolean | null
  rpo_met: boolean | null
  rto_met: boolean | null
  actual_rpo_hours: number | null
  actual_rto_hours: number | null
  lessons_learned: string | null
  postmortem_notes: string | null
  notes: string | null
  lead_engineer: string
  participants: string
}

interface ActionItem {
  id: number
  title: string
  description: string
  priority: string
  status: string
  assigned_to: string
  due_date: string | null
  completed_at: string | null
}

interface TimelineEntry {
  id: number
  event_type: string
  description: string
  occurred_at: string
  actor: string
}

// ── Color maps ────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  SCHEDULED: '#7C3AED',
  IN_PROGRESS: '#D97706',
  COMPLETED: '#16A34A',
  CANCELLED: '#6B7280',
  FAILED: '#DC2626',
}

const OUTCOME_COLORS: Record<string, string> = {
  PASS: '#16A34A',
  FAIL: '#DC2626',
  PARTIAL: '#D97706',
}

const PRIORITY_COLORS: Record<string, string> = {
  CRITICAL: '#DC2626',
  HIGH: '#EA580C',
  MEDIUM: '#D97706',
  LOW: '#6B7280',
}

const ACTION_STATUS_COLORS: Record<string, string> = {
  OPEN: '#7C3AED',
  IN_PROGRESS: '#D97706',
  CLOSED: '#16A34A',
}

const DRILL_TYPE_COLORS: Record<string, string> = {
  FULL_FAILOVER: '#1A7B7E',
  PARTIAL_FAILOVER: '#0891B2',
  TABLETOP: '#7C3AED',
  BACKUP_RESTORE: '#D97706',
  COMMUNICATION: '#EA580C',
}

// ── Helper components ─────────────────────────────────────────────────────────

function Badge({ label, color }: { label: string; color: string }) {
  return (
    <span
      className={styles.badge}
      style={{ '--bc': color } as React.CSSProperties}
    >
      {label}
    </span>
  )
}

function MetBadge({ met, label }: { met: boolean | null; label: string }) {
  if (met === null) return <span className={styles.notSet}>—</span>
  return met
    ? (
      <span className={styles.metBadgeGood}>
        <CheckCircle2 size={12} /> {label} Met
      </span>
    )
    : (
      <span className={styles.metBadgeBad}>
        <XCircle size={12} /> {label} Missed
      </span>
    )
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={styles.detailRow}>
      <span className={styles.detailLabel}>{label}</span>
      <span className={styles.detailValue}>{children}</span>
    </div>
  )
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function formatDateShort(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  })
}

function isOverdue(item: ActionItem): boolean {
  if (item.status === 'CLOSED' || !item.due_date) return false
  return new Date(item.due_date) < new Date()
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function DrillDetailPage() {
  const id = window.location.pathname.split('/').pop()

  const [drill, setDrill] = useState<DrillDetail | null>(null)
  const [actionItems, setActionItems] = useState<ActionItem[]>([])
  const [timeline, setTimeline] = useState<TimelineEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const headers = authHeaders()
      const [dRes, aRes, tRes] = await Promise.all([
        fetch(`${DR_API}/api/v1/dr/drills/${id}/`, { headers }),
        fetch(`${DR_API}/api/v1/dr/drills/${id}/action-items/`, { headers }),
        fetch(`${DR_API}/api/v1/dr/drills/${id}/timeline/`, { headers }),
      ])
      if (!dRes.ok) throw new Error(`Drill API error: ${dRes.status}`)
      const [dData, aData, tData] = await Promise.all([
        dRes.json(),
        aRes.ok ? aRes.json() : [],
        tRes.ok ? tRes.json() : [],
      ])
      setDrill(dData)
      setActionItems(Array.isArray(aData) ? aData : (aData.results ?? []))
      setTimeline(Array.isArray(tData) ? tData : (tData.results ?? []))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load drill details.')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { fetchAll() }, [fetchAll])

  function goBack() {
    window.location.href = '/disaster-recovery/drills'
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingRow}>
          <RefreshCw size={18} className={styles.spin} />
          <span>Loading drill details…</span>
        </div>
      </div>
    )
  }

  if (error || !drill) {
    return (
      <div className={styles.page}>
        <div className={styles.errorBanner}>
          <AlertTriangle size={15} />
          <span>{error ?? 'Drill not found.'}</span>
          <button className={styles.retryBtn} onClick={fetchAll}>Retry</button>
        </div>
      </div>
    )
  }

  const isCompleted = drill.status === 'COMPLETED'

  return (
    <div className={styles.page}>

      {/* ── Header ── */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.breadcrumb}>
            <Shield size={12} />
            <span>Aptean Platform</span>
            <ChevronRight size={11} />
            <span>Disaster Recovery</span>
            <ChevronRight size={11} />
            <span>Drills</span>
            <ChevronRight size={11} />
            <span className={styles.breadcrumbCurrent}>{drill.title}</span>
          </div>
          <div className={styles.titleRow}>
            <button className={styles.backBtn} onClick={goBack} title="Back to drills">
              <ArrowLeft size={16} />
            </button>
            <h1 className={styles.pageTitle}>{drill.title}</h1>
            <Badge
              label={drill.status.replace('_', ' ')}
              color={STATUS_COLORS[drill.status] ?? '#6B7280'}
            />
            <Badge
              label={drill.drill_type.replace(/_/g, ' ')}
              color={DRILL_TYPE_COLORS[drill.drill_type] ?? '#1A7B7E'}
            />
          </div>
          <p className={styles.productSub}>{drill.product_name}</p>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.btnSecondary} onClick={fetchAll}>
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {/* ── Detail + Outcome row ── */}
      <div className={styles.twoCol}>

        {/* Detail card */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <ClipboardList size={15} />
            <h2 className={styles.cardTitle}>Drill Details</h2>
          </div>
          <div className={styles.detailGrid}>
            <DetailRow label="Product">{drill.product_name}</DetailRow>
            <DetailRow label="Drill Type">
              <Badge label={drill.drill_type.replace(/_/g, ' ')} color={DRILL_TYPE_COLORS[drill.drill_type] ?? '#1A7B7E'} />
            </DetailRow>
            <DetailRow label="Scheduled Date">{formatDateShort(drill.scheduled_date)}</DetailRow>
            <DetailRow label="Started At">{formatDate(drill.started_at)}</DetailRow>
            <DetailRow label="Completed At">{formatDate(drill.completed_at)}</DetailRow>
            <DetailRow label="Duration">
              {drill.duration_minutes != null ? `${drill.duration_minutes} min` : '—'}
            </DetailRow>
            <DetailRow label="SLA Compliant">
              {drill.sla_compliant === null
                ? <span className={styles.notSet}>—</span>
                : drill.sla_compliant
                  ? <span className={styles.metBadgeGood}><CheckCircle2 size={12} /> Yes</span>
                  : <span className={styles.metBadgeBad}><XCircle size={12} /> No</span>}
            </DetailRow>
            <DetailRow label="Lead Engineer">
              <span className={styles.personTag}><User size={11} /> {drill.lead_engineer || '—'}</span>
            </DetailRow>
            <DetailRow label="Participants">
              {drill.participants
                ? <span className={styles.personTag}><Users size={11} /> {drill.participants}</span>
                : <span className={styles.notSet}>—</span>}
            </DetailRow>
            {drill.notes && (
              <div className={styles.notesRow}>
                <span className={styles.detailLabel}>Notes</span>
                <p className={styles.notesText}>{drill.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* RPO/RTO + Outcome stacked */}
        <div className={styles.rightStack}>

          {/* RPO / RTO */}
          {isCompleted && (
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <Activity size={15} />
                <h2 className={styles.cardTitle}>RPO / RTO Results</h2>
              </div>
              <div className={styles.rpoRtoGrid}>
                <div className={styles.rpoRtoBox}>
                  <div className={styles.rpoRtoBoxLabel}>RPO Target</div>
                  <div className={styles.rpoRtoBoxValue}>—</div>
                </div>
                <div className={styles.rpoRtoBox}>
                  <div className={styles.rpoRtoBoxLabel}>Actual RPO</div>
                  <div className={styles.rpoRtoBoxValue}>
                    {drill.actual_rpo_hours != null ? `${drill.actual_rpo_hours}h` : '—'}
                  </div>
                </div>
                <div className={styles.rpoRtoBox}>
                  <div className={styles.rpoRtoBoxLabel}>RTO Target</div>
                  <div className={styles.rpoRtoBoxValue}>—</div>
                </div>
                <div className={styles.rpoRtoBox}>
                  <div className={styles.rpoRtoBoxLabel}>Actual RTO</div>
                  <div className={styles.rpoRtoBoxValue}>
                    {drill.actual_rto_hours != null ? `${drill.actual_rto_hours}h` : '—'}
                  </div>
                </div>
              </div>
              <div className={styles.metRow}>
                <MetBadge met={drill.rpo_met} label="RPO" />
                <MetBadge met={drill.rto_met} label="RTO" />
              </div>
            </div>
          )}

          {/* Outcome */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <FileText size={15} />
              <h2 className={styles.cardTitle}>Outcome &amp; Postmortem</h2>
            </div>
            <div className={styles.outcomeSection}>
              <div className={styles.outcomeBadgeRow}>
                <span className={styles.detailLabel}>Outcome</span>
                {drill.outcome
                  ? <Badge label={drill.outcome} color={OUTCOME_COLORS[drill.outcome] ?? '#6B7280'} />
                  : <span className={styles.notSet}>Not recorded</span>}
              </div>
              {drill.lessons_learned && (
                <div className={styles.textBlock}>
                  <div className={styles.textBlockLabel}>Lessons Learned</div>
                  <p className={styles.textBlockContent}>{drill.lessons_learned}</p>
                </div>
              )}
              {drill.postmortem_notes && (
                <div className={styles.textBlock}>
                  <div className={styles.textBlockLabel}>Postmortem Notes</div>
                  <p className={styles.textBlockContent}>{drill.postmortem_notes}</p>
                </div>
              )}
              {!drill.lessons_learned && !drill.postmortem_notes && (
                <p className={styles.notSet}>No postmortem recorded yet.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Action Items ── */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <ClipboardList size={15} />
          <h2 className={styles.cardTitle}>Action Items</h2>
          <span className={styles.countChip}>{actionItems.length}</span>
        </div>
        {actionItems.length === 0 ? (
          <div className={styles.emptySection}>No action items recorded for this drill.</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Assigned To</th>
                  <th>Due Date</th>
                </tr>
              </thead>
              <tbody>
                {actionItems.map(item => (
                  <tr key={item.id} className={isOverdue(item) ? styles.overdueRow : ''}>
                    <td>
                      <div className={styles.actionTitle}>{item.title}</div>
                      {item.description && (
                        <div className={styles.actionDesc}>{item.description}</div>
                      )}
                    </td>
                    <td>
                      <Badge
                        label={item.priority}
                        color={PRIORITY_COLORS[item.priority] ?? '#6B7280'}
                      />
                    </td>
                    <td>
                      <Badge
                        label={item.status.replace('_', ' ')}
                        color={ACTION_STATUS_COLORS[item.status] ?? '#6B7280'}
                      />
                    </td>
                    <td>
                      {item.assigned_to
                        ? <span className={styles.personTag}><User size={11} /> {item.assigned_to}</span>
                        : <span className={styles.notSet}>—</span>}
                    </td>
                    <td>
                      <span className={isOverdue(item) ? styles.overdueDate : ''}>
                        {formatDateShort(item.due_date)}
                        {isOverdue(item) && (
                          <span className={styles.overdueTag}> Overdue</span>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Timeline ── */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <Clock size={15} />
          <h2 className={styles.cardTitle}>Event Timeline</h2>
          <span className={styles.countChip}>{timeline.length}</span>
        </div>
        {timeline.length === 0 ? (
          <div className={styles.emptySection}>No timeline events recorded.</div>
        ) : (
          <div className={styles.timeline}>
            {timeline.map((entry, idx) => (
              <div key={entry.id} className={styles.timelineItem}>
                <div className={styles.timelineTrack}>
                  <div className={styles.timelineDot} />
                  {idx < timeline.length - 1 && <div className={styles.timelineLine} />}
                </div>
                <div className={styles.timelineContent}>
                  <div className={styles.timelineHeader}>
                    <Badge label={entry.event_type.replace(/_/g, ' ')} color="#1A7B7E" />
                    <span className={styles.timelineTime}>
                      <Calendar size={11} /> {formatDate(entry.occurred_at)}
                    </span>
                    {entry.actor && (
                      <span className={styles.timelineActor}>
                        <User size={11} /> {entry.actor}
                      </span>
                    )}
                  </div>
                  <p className={styles.timelineDesc}>{entry.description}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  )
}
